// Phase 0084 — Role Responsibility Master API client.
import apiClient from '@/shared/api/client'

// ── Types ──────────────────────────────────────────────────────────────

export interface RoleMaster {
  id: string
  role_code: string
  designation_name: string
  department_id: string | null
  organization_unit_id: string | null
  designation_id: string | null
  reports_to_role_id: string | null
  role_level: string | null
  shift_type: string | null
  role_purpose: string | null
  approximate_manpower_min: number | null
  approximate_manpower_max: number | null
  criticality: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW'
  importance: string | null
  statutory_role_flag: boolean
  production_critical_flag: boolean
  safety_critical_flag: boolean
  requires_certification: boolean
  effective_from: string | null
  effective_to: string | null
  approval_status:
    | 'DRAFT'
    | 'PENDING_APPROVAL'
    | 'APPROVED'
    | 'REJECTED'
    | 'OBSOLETE'
    | 'DETAILS_PENDING'
  approved_by: string | null
  approved_at: string | null
  active: boolean
  current_version_no: number
  remarks: string | null
}

export interface ResponsibilityLine {
  id: string
  role_responsibility_id: string
  responsibility_code: string
  responsibility_type: string
  frequency: string
  responsibility_text: string
  priority: string | null
  evidence_required: boolean
  linked_kpi_id: string | null
  linked_checklist_id: string | null
  linked_module: string | null
  owner_role_id: string | null
  verifier_role_id: string | null
  escalation_role_id: string | null
  effective_from: string | null
  effective_to: string | null
  version_no: number
  sequence_no: number
  active: boolean
  remarks: string | null
}

export interface RoleBundle {
  master: RoleMaster
  reporting: Record<string, unknown> | null
  responsibility_lines: ResponsibilityLine[]
  kpis: Record<string, unknown>[]
  records: Record<string, unknown>[]
  authorities: Record<string, unknown>[]
  skills: Record<string, unknown>[]
  trainings: Record<string, unknown>[]
  compliance: Record<string, unknown>[]
  backups: Record<string, unknown>[]
}

export interface OrgUnit {
  id: string
  code: string
  name: string
  unit_type: string
  parent_id: string | null
  department_id: string | null
  plant_id: string | null
  active: boolean
  sort_order: number
  remarks: string | null
}

export interface OrgUnitNode extends OrgUnit {
  children: OrgUnitNode[]
}

export interface OrgChartNode extends RoleMaster {
  children: OrgChartNode[]
}

export interface VersionRow {
  id: string
  entity_type: string
  entity_id: string
  version_no: number
  old_value_json: Record<string, unknown> | null
  new_value_json: Record<string, unknown> | null
  change_reason: string | null
  changed_by: string | null
  changed_at: string
  approval_status: string
}

export type Kind =
  | 'lines'
  | 'kpis'
  | 'records'
  | 'authorities'
  | 'skills'
  | 'trainings'
  | 'compliance'
  | 'backups'

const BASE = '/hr/role-master'

// ── Role master ───────────────────────────────────────────────────────

export const roleMasterApi = {
  listRoles: async (active_only = true, approval_status?: string): Promise<RoleMaster[]> => {
    const params: Record<string, unknown> = { active_only }
    if (approval_status) params.approval_status = approval_status
    return (await apiClient.get<RoleMaster[]>(`${BASE}/roles`, { params })).data
  },
  getRole: async (roleIdOrCode: string): Promise<RoleBundle> =>
    (await apiClient.get<RoleBundle>(`${BASE}/roles/${roleIdOrCode}`)).data,
  createRole: async (body: Partial<RoleMaster>): Promise<RoleMaster> =>
    (await apiClient.post<RoleMaster>(`${BASE}/roles`, body)).data,
  updateRole: async (id: string, body: Partial<RoleMaster> & { change_reason?: string }): Promise<RoleMaster> =>
    (await apiClient.patch<RoleMaster>(`${BASE}/roles/${id}`, body)).data,
  deactivateRole: async (id: string, reason?: string): Promise<void> => {
    await apiClient.delete(`${BASE}/roles/${id}`, { params: reason ? { reason } : {} })
  },

  submitForApproval: async (id: string): Promise<RoleMaster> =>
    (await apiClient.post<RoleMaster>(`${BASE}/roles/${id}/submit`)).data,
  approve: async (id: string, reason?: string): Promise<RoleMaster> =>
    (await apiClient.post<RoleMaster>(`${BASE}/roles/${id}/approve`, { reason })).data,
  reject: async (id: string, reason: string): Promise<RoleMaster> =>
    (await apiClient.post<RoleMaster>(`${BASE}/roles/${id}/reject`, { reason })).data,

  orgChart: async (): Promise<OrgChartNode[]> =>
    (await apiClient.get<OrgChartNode[]>(`${BASE}/org-chart`)).data,

  // Satellite CRUD ── kind ∈ Kind
  listSatellite: async <T = Record<string, unknown>>(roleId: string, kind: Kind): Promise<T[]> =>
    (await apiClient.get<T[]>(`${BASE}/roles/${roleId}/${kind}`)).data,
  createSatellite: async <T = Record<string, unknown>>(
    roleId: string,
    kind: Kind,
    body: Record<string, unknown>,
  ): Promise<T> => (await apiClient.post<T>(`${BASE}/roles/${roleId}/${kind}`, body)).data,
  updateItem: async <T = Record<string, unknown>>(
    kind: Kind,
    id: string,
    body: Record<string, unknown>,
  ): Promise<T> => (await apiClient.patch<T>(`${BASE}/items/${kind}/${id}`, body)).data,
  deleteItem: async (kind: Kind, id: string, reason?: string): Promise<void> => {
    await apiClient.delete(`${BASE}/items/${kind}/${id}`, {
      params: reason ? { reason } : {},
    })
  },

  // Reporting matrix
  getReporting: async (roleId: string): Promise<Record<string, unknown> | null> =>
    (await apiClient.get(`${BASE}/roles/${roleId}/reporting`)).data,
  putReporting: async (
    roleId: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> =>
    (await apiClient.put(`${BASE}/roles/${roleId}/reporting`, body)).data,

  // Versions
  listVersions: async (entity_type?: string, entity_id?: string): Promise<VersionRow[]> => {
    const params: Record<string, unknown> = {}
    if (entity_type) params.entity_type = entity_type
    if (entity_id) params.entity_id = entity_id
    return (await apiClient.get<VersionRow[]>(`${BASE}/versions`, { params })).data
  },

  // Organisation units
  listUnits: async (): Promise<OrgUnit[]> =>
    (await apiClient.get<OrgUnit[]>(`${BASE}/org-units`)).data,
  unitTree: async (): Promise<OrgUnitNode[]> =>
    (await apiClient.get<OrgUnitNode[]>(`${BASE}/org-units/tree`)).data,
  createUnit: async (body: Partial<OrgUnit>): Promise<OrgUnit> =>
    (await apiClient.post<OrgUnit>(`${BASE}/org-units`, body)).data,
}
